import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useLang } from "../../i18n/lang";

const NAV = [["stones", "/stones"], ["bespoke", "/bespoke-objects"], ["process", "/process"], ["about", "/about"]];

const LangSwitch = ({ light }) => {
  const { lang } = useLang();
  const { pathname, search } = useLocation();
  const swap = (l) => pathname.replace(/^\/(en|it)/, `/${l}`) + search;
  return (
    <div className="flex items-center gap-2 eyebrow" data-testid="lang-switch">
      {["it", "en"].map((l, i) => (
        <span key={l} className="flex items-center gap-2">
          {i > 0 && <span className={light ? "text-[#FFFFFF]/50" : "text-[#A3A3A3]"}>/</span>}
          <Link to={swap(l)} data-testid={`lang-${l}`} aria-current={lang === l ? "true" : undefined}
            className={`transition-opacity duration-300 ${lang === l ? "opacity-100" : "opacity-55 hover:opacity-100"}`}>{l.toUpperCase()}</Link>
        </span>
      ))}
    </div>
  );
};

export const Header = ({ overlay }) => {
  const { t, to } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const light = overlay && !scrolled && !open;
  const solid = scrolled || !overlay;

  return (
    <>
      <header data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,color,border-color,padding] duration-700 ${solid && !open ? "bg-[#FFFFFF]/92 backdrop-blur-md border-b border-[#1A1A1A]/10 py-4" : "bg-transparent border-b border-transparent py-6"} ${light ? "text-[#FFFFFF]" : "text-[#1A1A1A]"}`}>
        <div className="px-6 sm:px-10 lg:px-14 flex items-center justify-between gap-6">
          <Link to={to("")} data-testid="header-logo" aria-label="THE STONE EDITION — home"><Logo /></Link>
          <nav className="hidden lg:flex items-center gap-10 text-[0.82rem] tracking-[0.08em]" aria-label="Main">
            {NAV.map(([k, p]) => (
              <NavLink key={k} to={to(p)} data-testid={`nav-${k}`} className="link-line"
                aria-current={pathname.startsWith(to(p)) ? "page" : undefined}>{t.nav[k]}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-6">
            <div className="hidden sm:block"><LangSwitch light={light} /></div>
            <Link to={to("/start-a-project")} data-testid="header-inquire"
              className={`hidden md:inline-flex items-center gap-3 border px-5 py-2.5 text-[0.78rem] tracking-[0.12em] uppercase transition-colors duration-500 ${light ? "border-[#FFFFFF]/60 hover:bg-[#FFFFFF] hover:text-[#1A1A1A]" : "border-[#1A1A1A]/70 hover:bg-[#1A1A1A] hover:text-[#FFFFFF]"}`}>
              {t.nav.inquire}
            </Link>
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-expanded={open}
              className="lg:hidden eyebrow py-2">{open ? t.nav.close : t.nav.menu}</button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div key="menu" data-testid="mobile-menu" data-lenis-prevent
            className="fixed inset-0 z-40 bg-[#FFFFFF] pt-28 px-6 sm:px-10 pb-10 flex flex-col justify-between lg:hidden"
            initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}>
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {[...NAV, ["contact", "/start-a-project"]].map(([k, p], i) => (
                <motion.div key={k} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.07, duration: 0.7 }}>
                  <Link to={to(p)} data-testid={`mobile-nav-${k}`} className="f-serif text-5xl font-light leading-tight flex items-baseline gap-4">
                    <span className="f-mono text-xs text-[#555555]">0{i + 1}</span>{t.nav[k]}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="flex items-center justify-between border-t border-[#1A1A1A]/15 pt-6">
              <LangSwitch />
              <Link to={to("/start-a-project")} className="eyebrow underline underline-offset-4" data-testid="mobile-inquire">{t.nav.inquire}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
